import React from 'react';
import { ScrollView } from 'react-native';
import { DNABox, DNAButton, DNAText, DNASlider, DNACheckbox, DNADivider } from '@alucio/lux-ui';
import DNAThumbnail from 'src/components/DNA/Thumbnail/DNAThumbnail';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import useMachineSelector, { composite } from 'src/hooks/useSelector';
import * as slideSettingsSelector from 'src/state/machines/publisherVersioning/SlideSettings/slideSettings.selectors';
import { useSlideSettingsState } from 'src/state/machines/publisherVersioning/SlideSettings/SlideSettingsProvider';
import { getThumbURL } from 'src/utils/thumbnailHelpers';
import {
  getVisibleSourcePages,
  isAllSlidesChecked,
} from 'src/state/machines/publisherVersioning/SlideSettings/util';
import ModalHeader from '../ModalHeader';
import { styles } from '../SlideSettings';

const SetRequiredSlidesModal: React.FC = () => {
  const {
    service,
    currentDocumentVersionORM,
    latestContentPageData,
    thumbnailSizes,
    getSlideTitle,
    getHasSlideTextInsertionEnabled,
    backToIdle,
    saveRequiredSlides,
    selectAllRequiredSlide,
    selectRequiredSlide,
  } = useSlideSettingsState()

  const cond = useMachineSelector(
    service,
    (state) => composite(
      state,
      slideSettingsSelector.versionDraft,
      slideSettingsSelector.selectedRequiredSlides,
      slideSettingsSelector.isSetRequiredSlidesMode,
      slideSettingsSelector.canSaveRequiredSlides,
    ),
  )

  const selectedPages = Object.values(cond.selectedRequiredSlides).filter(val => val)
  const visiblePages = getVisibleSourcePages(cond.versionDraft.pages);
  const selectedText = `${selectedPages.length} of ${visiblePages.length} Selected`
  const allSelected = isAllSlidesChecked(
    visiblePages,
    cond.selectedRequiredSlides,
  )

  return (
    <DNASlider
      visible={cond.isSetRequiredSlidesMode}
      setVisible={backToIdle}
      orientation="horizontal"
    >
      <DNABox
        fill
        appearance="col"
        style={{ backgroundColor: colors['color-text-white'] }}
      >
        <ModalHeader
          title="Which slides are required when building a custom presentation?"
          saveDisabled={!cond.canSaveRequiredSlides}
          onSave={saveRequiredSlides}
          onCancel={backToIdle}
        />

        {/* CONTENT */}
        <DNABox fill>
          <DNABox fill appearance="col">
            <DNABox style={styles.rowWrapper} alignY="center">
              <DNABox spacing="md" alignY="center">
                <DNACheckbox
                  checked={allSelected}
                  onChange={selectAllRequiredSlide}
                  style={{ marginLeft: 5, marginRight: 7 }}
                />
                <DNAText status="subtle">Select all</DNAText>
                <DNADivider style={styles.verticalDivider} />
                <DNAText status="subtle">
                  {selectedText}
                </DNAText>
              </DNABox>
              <DNABox fill alignX="end">
                <DNAButton
                  appearance="ghost"
                  status="tertiary"
                  onPress={thumbnailSizes.cycleThumbnailSize}
                  iconLeft={thumbnailSizes.thumbnailSize === 'lg' ? 'grid-nine' : 'squares-four'}
                />
              </DNABox>
            </DNABox>

            <DNABox fill>
              <ScrollView style={styles.content}>
                <DNABox
                  appearance="row"
                  wrap="start"
                  childStyle={{ marginBottom: 32 }}
                  alignX="center"
                >
                  {
                    visiblePages.map((page) => {
                      const coverThumbnailPageNumber = cond.versionDraft.selectedThumbnail ?? 1
                      const thumbURL = getThumbURL(currentDocumentVersionORM, page.number, latestContentPageData)
                      const isRequired = cond.selectedRequiredSlides[page.pageId]
                      const isCover = page.number === coverThumbnailPageNumber

                      return (
                        <DNABox
                          testID="required-slide"
                          key={thumbURL}
                          style={{ marginRight: 24, marginBottom: 24 }}
                        >
                          <DNAThumbnail
                            s3URL={thumbURL}
                            useLoadingIndicator
                            size={thumbnailSizes.thumbnailSize}
                            mode={DNAThumbnail.Modes.SELECTABLE}
                            variant={DNAThumbnail.Variants.INFO}
                            pageNumber={page.number}
                            checked={isRequired}
                            isCover={isCover}
                            isTextInsertionEnabled={getHasSlideTextInsertionEnabled(page.number)}
                            thumbnailTitle={getSlideTitle(page.number)}
                            isRequired={isRequired}
                            onCheck={() => selectRequiredSlide(page.pageId)}
                          />
                        </DNABox>
                      )
                    })
                  }
                </DNABox>
              </ScrollView>
            </DNABox>
          </DNABox>
        </DNABox>
      </DNABox>
    </DNASlider>
  )
}

export default SetRequiredSlidesModal
